import { Link, useParams } from 'react-router-dom';
import { PublicLayout } from '../components/Layout';
import { Skeleton } from '../components/primitives';
import { useSnapshot } from '../useSnapshot';
import { NotFound } from './NotFound';

export function SectionPage() {
  const { section } = useParams();
  const { snapshot, error, loading, retry } = useSnapshot();

  // Unknown sections (not in the published snapshot) get the branded 404.
  const sectionData = section && snapshot ? snapshot.sections[section] : undefined;
  if (snapshot && !sectionData) return <NotFound />;

  return (
    <PublicLayout>
      <div className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
        <nav aria-label="Breadcrumb" className="text-xs text-muted">
          <Link to="/" className="transition-colors hover:text-foreground">Knocking Forms</Link>
          <span aria-hidden> / </span>
          <span className="text-foreground">{sectionData?.label ?? section}</span>
        </nav>
        <h1 className="mt-4 text-3xl font-bold">{sectionData?.label ?? section}</h1>
        {sectionData?.description && (
          <p className="mt-2 text-sm leading-relaxed text-muted">{sectionData.description}</p>
        )}

        <div className="mt-10">
          {loading && (
            <div className="flex flex-col gap-4" aria-label="Loading forms">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}

          {error && (
            <div role="alert" className="rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm">
              <p>We couldn't load the forms right now ({error}).</p>
              <button type="button" onClick={retry} className="mt-2 font-medium underline underline-offset-2">
                Try again
              </button>
            </div>
          )}

          {sectionData && sectionData.forms.length === 0 && (
            <p className="rounded-xl border border-border bg-card px-4 py-8 text-center text-sm text-muted">
              No forms are published in this section right now.
            </p>
          )}

          {sectionData && sectionData.forms.length > 0 && (
            <ul className="flex flex-col gap-4">
              {sectionData.forms.map((form) => (
                <li key={form.slug}>
                  <Link
                    to={`/${form.slug}`}
                    className="group flex items-center justify-between gap-4 rounded-2xl border border-border bg-card px-5 py-5 transition-all hover:border-brand/50 hover:shadow-lg hover:shadow-brand/5 sm:px-6"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-base font-semibold transition-colors group-hover:text-brand">
                        {form.displayName}
                      </span>
                      {form.description && (
                        <span className="mt-1 block text-sm leading-snug text-muted">{form.description}</span>
                      )}
                    </span>
                    <span className="shrink-0 text-muted transition-transform group-hover:translate-x-1 group-hover:text-brand" aria-hidden>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14m-7-7 7 7-7 7" />
                      </svg>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
